import { styled, Theme } from '@mui/material/styles'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { mobile } from '../../utils/breakpoints'
import { SvgIconProps } from '@mui/material/SvgIcon'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import AddCommentIconComponent from '@mui/icons-material/AddComment'
import ChatIconComponent from '@mui/icons-material/Chat'
import ChatBubbleOutlineIconComponent from '@mui/icons-material/ChatBubbleOutline'
import MoreHorizIconComponent from '@mui/icons-material/MoreHoriz'
import SendIconComponent from '@mui/icons-material/Send'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded'
import Groups2RoundedIcon from '@mui/icons-material/Groups2Rounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import EventRepeatRounded from '@mui/icons-material/EventRepeatRounded'
import {
  AccountCircleRounded,
  EmailRounded,
  ManageAccountsRounded,
  SendAndArchiveRounded,
  SendRounded,
  HomeRounded,
  LogoutRounded,
  EditRounded,
  SaveRounded,
  PhoneRounded,
  PhoneIphoneRounded,
  PublicRounded,
  TuneRounded,
} from '@mui/icons-material'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import PersonAddRounded from '@mui/icons-material/PersonAddRounded'
import LinkedInIcon from '@mui/icons-material/LinkedIn'

import { ICON_FONT_SIZE_SMALL, ICON_FONT_SIZE_LARGE, ICON_FONT_SIZE_MEDIUM } from '../../theme'
/**
 * Mixin to return the color of the icon, or the default color if the color is not provided
 * @param theme - The theme of the application
 * @param color - The color of the icon
 * @returns The color of the icon, or the default color if the color is not provided
 */
const useColorOrDefault = (theme: Theme, color?: string, defaultColor = theme.palette.text.contrastText) => {
  if (!color) {
    return `
      color: ${defaultColor};
      .Mui-disabled & {
        color: ${theme.palette.action.disabled};
      }
    `
  }
  return `color: ${color};`
}

/**
 * Mixin to return the font size of the icon, or the default font size if the font size is not provided
 * @param theme - The theme of the application
 * @param fontSize - The font size of the icon ('small', 'large', or custom pixel value like '25px')
 * @returns The font size of the icon, or the default font size if the font size is not provided
 */
const useFontSizeOrDefault = (theme: Theme, fontSize?: string) => {
  if (!fontSize) {
    return `
     font-size: ${ICON_FONT_SIZE_LARGE};
    ${mobile(theme, { 'font-size': ICON_FONT_SIZE_SMALL })}
  `
  }

  let size: string
  switch (fontSize) {
    case 'small':
      size = ICON_FONT_SIZE_SMALL
      break
    case 'medium':
      size = ICON_FONT_SIZE_MEDIUM
      break
    case 'large':
      size = ICON_FONT_SIZE_LARGE
      break
    default:
      size = fontSize
      break
  }

  return `font-size: ${size};`
}

// Create a configuration array for the icons.
const iconComponents = [
  { name: 'InfoIcon', icon: InfoOutlinedIcon },
  { name: 'CloseIcon', icon: CloseRoundedIcon },
  { name: 'AddIcon', icon: AddRoundedIcon },
  { name: 'AddCommentIcon', icon: AddCommentIconComponent },
  { name: 'ChatIcon', icon: ChatIconComponent },
  { name: 'ChatBubbleOutlineIcon', icon: ChatBubbleOutlineIconComponent, darkByDefault: true },
  { name: 'CloseActionIcon', icon: CloseRoundedIcon, darkByDefault: true },
  { name: 'MoreHorizIcon', icon: MoreHorizIconComponent, darkByDefault: true },
  { name: 'SendIcon', icon: SendIconComponent, darkByDefault: true },
  { name: 'RemoveIcon', icon: RemoveRoundedIcon },
  { name: 'InsightsIcon', icon: BarChartRoundedIcon },
  { name: 'MembersIcon', icon: Groups2RoundedIcon },
  { name: 'SettingsIcon', icon: SettingsRoundedIcon },
  { name: 'DeleteIcon', icon: DeleteRoundedIcon },
  { name: 'EmailIcon', icon: EmailRoundedIcon },
  { name: 'SendTestIcon', icon: SendAndArchiveRounded },
  { name: 'SendMessageIcon', icon: SendRounded },
  { name: 'NewsletterIcon', icon: EmailRounded },
  { name: 'ProfileIcon', icon: ManageAccountsRounded },
  { name: 'SubscriptionIcon', icon: EventRepeatRounded },
  { name: 'AvatarIcon', icon: AccountCircleRounded },
  { name: 'HomeIcon', icon: HomeRounded },
  { name: 'LogoutIcon', icon: LogoutRounded },
  { name: 'AddMemberIcon', icon: PersonAddRounded },
  { name: 'EditIcon', icon: EditRounded },
  { name: 'PreviewIcon', icon: VisibilityRoundedIcon },
  { name: 'SaveIcon', icon: SaveRounded },
  { name: 'PhoneIcon', icon: PhoneRounded },
  { name: 'MobileIcon', icon: PhoneIphoneRounded },
  { name: 'WebsiteIcon', icon: PublicRounded },
  { name: 'LinkedIn', icon: LinkedInIcon },
  { name: 'FilterIcon', icon: TuneRounded },
]

// Create an object to store the styled components.
const styledIcons: { [key: string]: React.ComponentType<SvgIconProps & { customColor?: string; size?: string | 'small' | 'large' }> } = {}

// Loop through the configuration and create each styled component.
iconComponents.forEach(({ name, icon, darkByDefault }) => {
  styledIcons[name] = styled(icon, {
    shouldForwardProp: (prop) => prop !== 'customColor' && prop !== 'size',
  })<{ customColor?: string; size?: string }>`
    ${({ theme, customColor }) => useColorOrDefault(theme, customColor, darkByDefault ? theme.palette.text.primary : undefined)}
    ${({ theme, size }) => useFontSizeOrDefault(theme, size)}
  `
})

// Destructure the styled icons object for named exports.
export const {
  ProfileIcon,
  MembersIcon,
  SettingsIcon,
  SendTestIcon,
  SendMessageIcon,
  NewsletterIcon,
  CloseIcon,
  AddIcon,
  AddCommentIcon,
  ChatIcon,
  ChatBubbleOutlineIcon,
  CloseActionIcon,
  MoreHorizIcon,
  SendIcon,
  RemoveIcon,
  InsightsIcon,
  DeleteIcon,
  EmailIcon,
  PhoneIcon,
  SubscriptionIcon,
  InfoIcon,
  AvatarIcon,
  HomeIcon,
  LogoutIcon,
  AddMemberIcon,
  EditIcon,
  PreviewIcon,
  SaveIcon,
  MobileIcon,
  WebsiteIcon,
  LinkedIn,
  FilterIcon,
} = styledIcons
